import React, { useEffect, useRef, useState } from "react";
import "../../App.css";


// MD Vision Section Component
function MDVisionSection() {
  const visionData = {
    mission: "To be the leading geospatial technology solutions provider, empowering organizations with cutting-edge mapping, analytics, and location intelligence services that drive informed decision-making and sustainable growth.",

    vision: "Revolutionizing the way businesses and governments harness the power of location data to solve complex challenges, create innovative solutions, and build a smarter, more connected world through advanced geospatial technologies.",

    values: [
      {
        title: "Innovation",
        description: "Continuously pushing boundaries with cutting-edge geospatial technologies and creative solutions.",
        image: "/values/value-1.png"
      },
      {
        title: "Excellence",
        description: "Delivering superior quality services and maintaining the highest standards in everything we do.",
        image: "/values/value-2.png"
      },
      {
        title: "Integrity",
        description: "Building trust through transparency, honesty, and ethical business practices.",
        image: "/values/value-3.png"
      },
      {
        title: "Collaboration",
        description: "Working together with clients, partners, and communities to achieve shared success.",
        image: "/values/value-4.png"
      }
    ],

    mdMessage: "At GEOTECH, we believe that location intelligence is the key to unlocking unprecedented opportunities. Our commitment goes beyond providing technology – we deliver insights that transform challenges into opportunities, data into decisions, and vision into reality. Together, we're not just mapping the world; we're shaping its future.",

    mdInfo: {
      name: "RaviKumar Veeramani",
      position: "Founder & Managing Director",
      experience: "25+ years in IT and Geospatial Technologies",
      description: "RaviKumar Veeramani serves as both the Founder and a key leader responsible for Geotech's ongoing achievements. With a strong academic foundation in Applied Geology and over 25 years of experience in IT and Geospatial Technologies, he effectively combines technical expertise with strategic vision.\n\nHis commitment to client focus, technical excellence, and integrity has shaped the core values of the organisation. As a proponent of continuous learning, he actively participates in international forums, remains informed about global industry trends, and provides guidance to emerging professionals.",
      quote: "Growth is not just about success; it is about alignment: remaining true to one's core values while advancing in skill, perspective, and influence."
    }
  };

  const valuesSectionRef = useRef(null);
  const [animatedCards, setAnimatedCards] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (valuesSectionRef.current) {
        const sectionTop = valuesSectionRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        if (sectionTop < windowHeight * 0.75 && animatedCards === 0) {
          setAnimatedCards(1);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [animatedCards]);

  useEffect(() => {
    let timer;
    if (animatedCards > 0 && animatedCards < visionData.values.length + 1) {
      timer = setTimeout(() => {
        setAnimatedCards(prev => prev + 1);
      }, animatedCards === 1 ? 800 : 600);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [animatedCards, visionData.values.length]);

  return (
    <section className="md-vision-section">
      <div className="section__container">
        {/* MD Section Header */}
        <div className="md-intro-header">
          <h2 className="section__title1">At the Core of Our Journey</h2>
        </div>

        {/* MD Introduction Section - Photo Left, Content Right */}
        <div className="md-vision-hero">
          <div className="md-photo-container">
            <div className="md-photo">
              <img
                src="/md.png"
                alt="Managing Director"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="md-photo-placeholder" style={{display: 'none'}}>
                <span className="md-initials">MD</span>
              </div>
              
              {/* Name Label Overlay */}
              <div className="md-photo-label">
                <p className="md-photo-name">{visionData.mdInfo.name}</p>
                <p className="md-photo-title">{visionData.mdInfo.position}</p>
              </div>
            </div>
          </div>

          <div className="md-content">
            <div className="md-bio-card">
              <h3>{visionData.mdInfo.name}</h3>
              <h4>{visionData.mdInfo.position}</h4>
              <div className="md-experience">
                <span className="experience-badge">{visionData.mdInfo.experience}</span>
              </div>
              <p className="md-description">
                {visionData.mdInfo.description}
              </p>
              
              {/* MD Quote */}
              <div className="md-quote">
                <svg className="quote-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.5 10c-2.5 0-4.5 2-4.5 4.5s2 4.5 4.5 4.5c1 0 2-.3 2.8-.9-.4 2.1-2.3 3.7-4.6 3.9v2.3c4.1-.4 7.3-3.8 7.3-8v-10h-5.5v4.2zm11 0c-2.5 0-4.5 2-4.5 4.5s2 4.5 4.5 4.5c1 0 2-.3 2.8-.9-.4 2.1-2.3 3.7-4.6 3.9v2.3c4.1-.4 7.3-3.8 7.3-8v-10h-5.5v4.2z"/>
                </svg>
                <p className="quote-text">"{visionData.mdInfo.quote}"</p>
                <div className="quote-author">
                  <div className="quote-line"></div>
                  <span>{visionData.mdInfo.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="values-section" ref={valuesSectionRef}>
          <h3>Our Core Values</h3>
          <div className="values-grid">
            {visionData.values.map((value, index) => (
              <div 
                key={index} 
                className={`value-card ${index < animatedCards ? 'animated' : ''}`}
              >
                <div className="value-card__image">
                  <img 
                    src={value.image} 
                    alt={value.title}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.classList.add('image-error');
                    }}
                  />
                </div>
                <div className="value-card__header">
                  <h4>{value.title}</h4>
                </div>
                <div className="value-card__content">
                  <p>{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default MDVisionSection;